import _ from 'lodash'
import { withDependencies, named } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor, Callback } from 'thunderbolt-viewer-manager-types'
import {
	EditorFeatureConfigSymbol,
	Props,
	IPropsStore,
	CompProps,
	IStructureAPI,
	StructureAPI,
	BeckyPropsInitialStoreSym,
	Component,
	ControllersInvoker,
	ControllersInvokerSymbol,
	CarmiInstance,
} from '@wix/thunderbolt-symbols'
import { ISessionManager, SessionManagerSymbol } from 'feature-session-manager'
import type { DSCarmi, PlatformDsModel } from 'thunderbolt-ds-carmi-root'
import { name, ViewerManagerPlatformApplicationRequestsAPISymbol } from './symbols'
import type { ViewerManagerPlatformApiEditorConfig, IViewerManagerPlatformApplicationRequestsAPI } from './types'

const mapPropsToState = (compType: string, props: CompProps) => {
	const propsWithoutFunctions = _.omitBy(props, (prop) => _.isFunction(prop))
	switch (compType) {
		case 'StateBox':
		case 'SlideShowContainer':
			return { ...propsWithoutFunctions, currentIndex: propsWithoutFunctions.currentSlideIndex }
		default:
			return props
	}
}

const validateCompDataByCompType = (comp: Component) => comp && comp.componentType === 'Repeater'

export const ViewerManagerPlatformApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		CarmiInstance,
		ViewerManagerPlatformApplicationRequestsAPISymbol,
		Props,
		StructureAPI,
		BeckyPropsInitialStoreSym,
		SessionManagerSymbol,
		ControllersInvokerSymbol,
	],
	(
		viewerManagerPlatformApiConfig: ViewerManagerPlatformApiEditorConfig,
		{ platform: platformSetters }: DSCarmi,
		viewerManagerPlatformApplicationRequestsAPI: IViewerManagerPlatformApplicationRequestsAPI,
		propsStore: IPropsStore,
		structureApi: IStructureAPI,
		beckyPropsInitialStore: IPropsStore,
		sessionManager: ISessionManager,
		controllersInvoker: ControllersInvoker
	): IViewerManagerImplementor<'platform'> & IViewerManagerImplementor<'platformDsModel'> => {
		return {
			platformDsModel: {
				getPlatformDsModel(key: keyof PlatformDsModel) {
					const platformDsModel = viewerManagerPlatformApiConfig.dsModel
					return platformDsModel[key]
				},
				updatePlatformDsModel: (key: keyof PlatformDsModel, value) => {
					platformSetters.setDsModel(key, value)
				},
			},
			platform: {
				triggerAppStudioWidgetOnPropsChanged(pageId, widgetId, newProps) {
					controllersInvoker.invokePropsSetters(widgetId, newProps)
				},
				getComponentsByPageIdForWixCode(pageId: string) {
					return viewerManagerPlatformApiConfig.pagesIdsToDescendants[pageId]
				},
				registerToAppInstanceUpdate(callback: Callback) {
					sessionManager.addLoadNewSessionCallback(callback)
				},
				registerToNotifyApplicationRequestFromViewerWorker(cb: Callback) {
					viewerManagerPlatformApplicationRequestsAPI.registerCallback(cb)
				},
				// @ts-ignore
				getRuntimeDal() {
					return {
						getCompState(compId) {
							const comp = structureApi.get(compId)
							if (!comp) {
								return {}
							}
							const compType = comp.componentType
							return {
								id: compId,
								...mapPropsToState(compType, propsStore.get(compId)),
							}
						},
						hasRuntimeChanges(compId) {
							const propsWithoutFunctions = _.omitBy(propsStore.get(compId), (prop) => _.isFunction(prop))
							return !_.isEqual(propsWithoutFunctions, beckyPropsInitialStore.get(compId))
						},
						getCompData(compId) {
							if (validateCompDataByCompType(structureApi.get(compId))) {
								return propsStore.get(compId)
							}
						},
					}
				},
			},
		}
	}
)
